/**
 * What a rack is made of.
 *
 * One colour is stored and every surface of the rack is derived from it, rather
 * than a set of colours being stored per face: a rack is one object, and a
 * palette that let its front, its edge and its recess drift apart would let a
 * user build something that does not read as a solid thing at all.
 *
 * The derivation is `color-mix` in the browser rather than arithmetic here,
 * because it has to happen in the theme the page is actually being drawn in —
 * the same colour has to hold its edges against a light background and a dark
 * one, which is a question CSS already answers for every other surface here.
 */

/** What a rack is finished in when its owner has not said. */
export const DEFAULT_SHELF_COLOR = "#3f4756";

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/**
 * A colour this board is willing to paint a rack in.
 *
 * Anything else reads as the default rather than as a rack drawn in whatever a
 * malformed value happens to mean to CSS: a Shelf arranged on a desktop, or by
 * a later version of it, must never be able to make a rack invisible here.
 */
export const normalizeShelfColor = (color: string | null | undefined) =>
  color !== undefined && color !== null && HEX_COLOR.test(color)
    ? color.toLowerCase()
    : DEFAULT_SHELF_COLOR;

/**
 * The one thing about a rack that cannot be derived in CSS: whether the writing
 * on it should be light or dark.
 *
 * Every other surface is a `color-mix` off the stored colour, because those are
 * questions about how a shade sits against the theme the page is drawn in. This
 * one is a question about the rack's **own** colour, which is a number this
 * process already has — and CSS has no portable way to branch on it. Left to
 * white always, a rack painted in a pale custom colour has its name written in
 * white on near-white.
 *
 * The threshold is WCAG's own crossover point, where black and white text carry
 * the same contrast against a background.
 */
const WHITE_TEXT_LUMINANCE_CROSSOVER = 0.179;

const channelLuminance = (channel: number) => {
  const normalized = channel / 255;
  return normalized <= 0.04045 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4);
};

/** WCAG relative luminance of a `#rrggbb` colour. */
export const resolveShelfColorLuminance = (color: string | null | undefined) => {
  const hex = normalizeShelfColor(color).slice(1);
  const red = Number.parseInt(hex.slice(0, 2), 16);
  const green = Number.parseInt(hex.slice(2, 4), 16);
  const blue = Number.parseInt(hex.slice(4, 6), 16);
  return (
    0.2126 * channelLuminance(red) +
    0.7152 * channelLuminance(green) +
    0.0722 * channelLuminance(blue)
  );
};

/** Which way round the writing on a rack of this colour has to be. */
export const resolveShelfInkTone = (color: string | null | undefined) =>
  resolveShelfColorLuminance(color) > WHITE_TEXT_LUMINANCE_CROSSOVER ? "dark" : "light";

/** Below this, a finish is too close to black to cut anything darker from it. */
const NEAR_BLACK_LUMINANCE = 0.03;

/**
 * Whether a finish is near-black, which is the one case where a rack's inside
 * cannot be cut darker than its frame. Held to the desktop's threshold, so a
 * rack reads the same where it was built and where it is looked at.
 */
export const isShelfFinishNearBlack = (color: string | null | undefined) =>
  resolveShelfColorLuminance(color) < NEAR_BLACK_LUMINANCE;
